import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, FormInput, Button } from './index';
import SectionPanel from './SectionPanel';
import { WidgetActionRow } from './WidgetActionRow';
import { useAuth } from '../context/AuthContext';
import { personApi, userPreferencesApi } from '../services/api';
import { validateInviteFields } from '../utils/inviteValidation';

interface CompanyInviteWidgetProps {
  /** Show the introductory paragraphs above the form (used on the Bjud in page). */
  showIntro?: boolean;
  /** Dense single-row layout used by the start-page widget. */
  compact?: boolean;
  className?: string;
  /** Render the collapsible header + chevron and persist the state per user. */
  collapsible?: boolean;
  /** Render inside the shared dark-blue SectionPanel banner ("Bjud in
   * byggföretag") instead of the widget's own in-body heading. */
  banner?: boolean;
}

const INVITE_FIELD_NAMES = {
  name: 'companyName',
  email: 'contactEmail',
  phone: 'contactPhone',
} as const;

/**
 * Shared "Bjud in företag" form for private persons inviting a construction
 * company. Used both as a dense, collapsible widget at the top of the person
 * start page and as the full form on the standalone Bjud in page — same
 * component, so the two stay identical.
 */
export function CompanyInviteWidget({
  showIntro = true,
  compact = false,
  className,
  collapsible = false,
  banner = false,
}: CompanyInviteWidgetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const emptyForm = { companyName: '', contactEmail: '', contactPhone: '' };
  const [inviteForm, setInviteForm] = useState(emptyForm);
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [isInviting, setIsInviting] = useState(false);
  const [sent, setSent] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  // Collapsible support. A first-paint hint in localStorage avoids an
  // open→collapse flash; the per-user server preference is authoritative so the
  // choice follows the logged-in person across browsers.
  const [collapsed, setCollapsed] = useState(
    () => collapsible && localStorage.getItem('personInviteWidgetCollapsedHint') === 'true'
  );
  const [collapseAnimate, setCollapseAnimate] = useState(false);

  const setCollapsedByUser = (c: boolean) => {
    setCollapseAnimate(true);
    setCollapsed(c);
    localStorage.setItem('personInviteWidgetCollapsedHint', String(c));
    userPreferencesApi.savePreference('personInviteWidgetCollapsed', c).catch(() => {});
  };

  useEffect(() => {
    if (!collapsible) return;
    userPreferencesApi.getPreferences().then((prefs) => {
      if (typeof prefs.personInviteWidgetCollapsed === 'boolean') {
        setCollapsed(prefs.personInviteWidgetCollapsed);
        localStorage.setItem('personInviteWidgetCollapsedHint', String(prefs.personInviteWidgetCollapsed));
      }
    }).catch(() => {});
  }, [collapsible]);

  const handleInviteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextValue = value;
    setInviteForm((prev) => ({ ...prev, [name]: nextValue }));
    if (inviteErrors[name]) {
      setInviteErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate a single field on blur so the user gets immediate, per-field
  // feedback instead of a wall of errors only at submit.
  const handleInviteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const field = e.target.name;
    const errors = validateInviteFields(
      { name: inviteForm.companyName, email: inviteForm.contactEmail, phone: inviteForm.contactPhone },
      INVITE_FIELD_NAMES
    );
    setInviteErrors((prev) => ({ ...prev, [field]: errors[field] || '' }));
  };

  const validateInviteForm = (): boolean => {
    const errors = validateInviteFields(
      { name: inviteForm.companyName, email: inviteForm.contactEmail, phone: inviteForm.contactPhone },
      INVITE_FIELD_NAMES
    );
    setInviteErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInviteForm()) return;

    setIsInviting(true);
    setInviteMessage(null);

    try {
      const result = await personApi.inviteCompany({
        companyName: inviteForm.companyName || undefined,
        contactPhone: inviteForm.contactPhone || undefined,
        contactEmail: inviteForm.contactEmail,
      });
      // A contractor that is already verified is connected at once: there is
      // nothing to wait for and no link to share (ClickUp 869fj4t2h).
      if (result.outcome === 'already_linked') {
        setInviteMessage({ type: 'success', text: t('personHome.invite.alreadyLinked', { company: result.companyName || t('personHome.invite.theContractor') }) });
        setInvitationUrl(null);
      } else if (result.outcome === 'linked') {
        const company = result.companyName || t('personHome.invite.theContractor');
        const text = [
          t('personHome.invite.linked', { company }),
          result.emailStatus === 'sent' ? t('personHome.invite.linkedEmailSent') : t('personHome.invite.linkedEmailFailed')
        ].join(' ');
        setInviteMessage({ type: 'success', text });
        setInvitationUrl(null);
      } else {
        const msg =
          result.emailStatus === 'sent'
            ? t('personHome.invite.emailSent')
            : t('personHome.invite.emailFailed');
        setInviteMessage({ type: 'success', text: msg });
        setInvitationUrl(result.registrationUrl || null);
      }
      // Clear the form on success; keep it untouched on a technical error.
      setInviteForm(emptyForm);
      setInviteErrors({});
      setSent(true);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      setInviteMessage({ type: 'error', text: t('personHome.invite.error') });
      setInvitationUrl(null);
    } finally {
      setIsInviting(false);
    }
  };

  const handleInviteAnother = () => {
    setSent(false);
    setInviteMessage(null);
    setInvitationUrl(null);
    setUrlCopied(false);
  };

  const handleCopyUrl = () => {
    if (invitationUrl) {
      navigator.clipboard.writeText(invitationUrl).then(() => {
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
      });
    }
  };

  const heading = (
    <>
      <i className="feather-briefcase me-2"></i>
      {t('personHome.invite.title')}
    </>
  );

  // In the narrow banner column each field gets its own row; the standalone
  // compact layout keeps three fields per row on md+.
  const fieldColClass = banner ? 'col-12' : 'col-12 col-md-4';

  const toggleButton = collapsible ? (
    <button
      type="button"
      className="btn btn-sm btn-light-primary flex-shrink-0"
      onClick={() => setCollapsedByUser(!collapsed)}
      aria-expanded={!collapsed}
      aria-label={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
      title={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
    >
      <i className={`feather-chevron-${collapsed ? 'down' : 'up'}`}></i>
    </button>
  ) : null;

  const collapsibleContent = (
      <div
        aria-hidden={collapsible ? collapsed : undefined}
        style={collapsible ? {
          maxHeight: collapsed ? 0 : 2000,
          opacity: collapsed ? 0 : 1,
          overflow: 'hidden',
          transition: collapseAnimate ? 'max-height 2s ease, opacity 2s ease' : 'none',
        } : undefined}
      >
        {showIntro && (
          <>
            <div className="mb-4">
              <p>{t('personHome.systemCompany.noLink')}</p>
              <p>{t('personHome.systemCompany.inviteInfo')}</p>
              <p className="mb-0">{t('personHome.systemCompany.contact')}</p>
            </div>
            <hr className="my-4" />
          </>
        )}

        {!collapsible && (
          <h5 className={`fw-bold ${compact ? 'mb-3' : 'mb-4'}`}>{heading}</h5>
        )}

        {inviteMessage && (
          <div className={`alert alert-${inviteMessage.type === 'success' ? 'success' : 'danger'} mb-3`}>
            {inviteMessage.text}
          </div>
        )}

        {invitationUrl && (
          <div className="mb-3">
            <label className="form-label fw-bold">{t('personHome.invite.urlLabel')}</label>
            <div className="input-group">
              <input type="text" className="form-control" value={invitationUrl} readOnly />
              <button className="btn btn-outline-primary" type="button" onClick={handleCopyUrl}>
                <i className={`feather-${urlCopied ? 'check' : 'copy'} me-1`}></i>
                {urlCopied ? t('personHome.invite.urlCopied') : t('common.copy', 'Kopiera')}
              </button>
            </div>
          </div>
        )}

        {sent ? (
          <WidgetActionRow>
            <Button type="button" variant="light-brand" onClick={handleInviteAnother}>
              <i className="feather-plus me-2"></i>
              {t('personHome.invite.inviteAnother')}
            </Button>
          </WidgetActionRow>
        ) : (
          <form onSubmit={handleInviteSubmit} noValidate className={compact ? 'invite-widget-dense' : undefined}>
            {compact ? (
              <>
                <div className="row g-2 align-items-start">
                  <div className={fieldColClass}>
                    <FormInput
                      id="invite-company-name"
                      name="companyName"
                      label={t('personHome.invite.companyName')}
                      value={inviteForm.companyName}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      placeholder={t('personHome.invite.companyNamePlaceholder')}
                      error={inviteErrors.companyName ? t(inviteErrors.companyName) : undefined}
                      required
                    />
                  </div>
                  <div className={fieldColClass}>
                    <FormInput
                      name="contactEmail"
                      type="email"
                      label={t('personHome.invite.contactEmail')}
                      value={inviteForm.contactEmail}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      error={inviteErrors.contactEmail ? t(inviteErrors.contactEmail) : undefined}
                      required
                    />
                  </div>
                  <div className={fieldColClass}>
                    <FormInput
                      type="tel"
                      name="contactPhone"
                      label={t('personHome.invite.contactPhone')}
                      value={inviteForm.contactPhone}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      placeholder={t('personHome.invite.contactPhonePlaceholder')}
                      error={inviteErrors.contactPhone ? t(inviteErrors.contactPhone) : undefined}
                    />
                  </div>
                </div>
                <WidgetActionRow>
                  <Button type="submit" isLoading={isInviting}>
                    <i className="feather-send me-2"></i>
                    {t('personHome.invite.submit')}
                  </Button>
                </WidgetActionRow>
              </>
            ) : (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      name="inviterName"
                      label={t('personHome.invite.yourName')}
                      value={user?.fullName || ''}
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      name="companyName"
                      label={t('personHome.invite.companyName')}
                      value={inviteForm.companyName}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      placeholder={t('personHome.invite.companyNamePlaceholder')}
                      error={inviteErrors.companyName ? t(inviteErrors.companyName) : undefined}
                      required
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <FormInput
                      name="contactEmail"
                      type="email"
                      label={t('personHome.invite.contactEmail')}
                      value={inviteForm.contactEmail}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      error={inviteErrors.contactEmail ? t(inviteErrors.contactEmail) : undefined}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <FormInput
                      type="tel"
                      name="contactPhone"
                      label={t('personHome.invite.contactPhone')}
                      value={inviteForm.contactPhone}
                      onChange={handleInviteChange}
                      onBlur={handleInviteBlur}
                      placeholder={t('personHome.invite.contactPhonePlaceholder')}
                      error={inviteErrors.contactPhone ? t(inviteErrors.contactPhone) : undefined}
                    />
                    <small className="text-muted" style={{ marginTop: '-10px', display: 'block' }}>
                      {t('personHome.invite.contactPhoneHelp')}
                    </small>
                  </div>
                </div>

                <WidgetActionRow className="mt-4">
                  <Button type="submit" isLoading={isInviting}>
                    <i className="feather-send me-2"></i>
                    {t('personHome.invite.submit')}
                  </Button>
                </WidgetActionRow>
              </>
            )}
          </form>
        )}
      </div>
  );

  if (banner) {
    return (
      <SectionPanel
        title={t('personHome.invite.title')}
        icon="user-plus"
        className={className}
        fillHeight={false}
        headerAction={toggleButton}
      >
        {collapsibleContent}
      </SectionPanel>
    );
  }

  return (
    <Card className={className}>
      {collapsible && (
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">{heading}</h5>
          {toggleButton}
        </div>
      )}
      {collapsibleContent}
    </Card>
  );
}
